import React, { useState } from 'react';
import { glassSurface } from './config/glassTheme';
import { useNavigate } from 'react-router-dom';
import ProductNav from './components/ProductNav';
import SideHelpPanel from './components/SideHelpPanel';

// The Battlemaps creation suite. Exported so the Battlemaps home page renders
// the same five engines from the same definition - the descriptions used to be
// duplicated, which is how Cinematic ended up described two different ways.
export const BATTLEMAP_ENGINES = [
  { key: 'tactical',   name: 'Tactical Battlemaps',      desc: 'Generate fully playable, grid-aligned battlemaps designed for virtual tabletop systems.',       points: ['Full encounter-ready maps', 'Consistent terrain scaling', 'Grid-aligned composition', 'Optimised for VTT platforms'],                        route: '/generators?library=battlemaps&category=tactical',   icon: '🗺️', color: '#D4AF37', gradient: 'linear-gradient(135deg, #D4AF37, #b8962e)',   colSpan: 3 },
  { key: 'cinematic',  name: 'Cinematic Scenes',          desc: 'The dedicated video-native scene engine. Compose a scene across six layers and generate moving footage of it.', points: ['Generates video, not still images', 'Six-layer scene composition', 'Subject, action, target and interaction relationships', 'Camera movement, framing and lighting control'], route: '/generators?library=battlemaps&category=cinematic',  icon: '🎬', color: '#a855f7', gradient: 'linear-gradient(135deg, #7c3aed, #a855f7)',   colSpan: 3, video: true },
  { key: 'structures', name: 'Structure Assets',          desc: 'Create modular buildings and structures for use in tactical battlemaps and encounter design.',     points: ['Placeable structures', 'Clean top-down assets', 'Modular layouts', 'Scalable for all environments'],                                       route: '/generators?library=battlemaps&category=structures', icon: '🏰', color: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6, #a855f7)',   colSpan: 2 },
  { key: 'props',      name: 'Props & Objects',           desc: 'Generate detailed props and encounter objects for enhancing tactical map gameplay.',              points: ['Scatter props', 'Encounter objects', 'Environmental storytelling assets', 'Ready for direct placement'],                                   route: '/generators?library=battlemaps&category=props',      icon: '⚗️', color: '#0ea5e9', gradient: 'linear-gradient(135deg, #0ea5e9, #0284c7)',   colSpan: 2 },
  { key: 'terrain',    name: 'Terrain Tiles',             desc: 'Build seamless terrain tiles and environmental segments for custom map construction.',            points: ['Tile-based environments', 'Seamless edge matching', 'Multi-biome support', 'Map-building workflows'],                                      route: '/generators?library=battlemaps&category=terrain',    icon: '🌿', color: '#10b981', gradient: 'linear-gradient(135deg, #10b981, #059669)',   colSpan: 2 },
];

function BattlemapEngineBox({ engine, onNavigate }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{ ...bm.box, ...(hovered ? bm.boxHover : {}), borderColor: hovered ? engine.color : 'rgba(255,255,255,0.12)', boxShadow: hovered ? `0 12px 40px rgba(0,0,0,0.35), 0 0 0 1px ${engine.color}44` : '0 8px 32px rgba(0,0,0,0.2)', gridColumn: `span ${engine.colSpan}` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onNavigate(engine.route)}
    >
      <div style={bm.boxTop}>
        <div style={{ ...bm.boxIcon, background: engine.gradient }}>{engine.icon}</div>
        <div>
          <div style={bm.boxName}>{engine.name}</div>
          <div style={{ ...bm.liveTag, background: `${engine.color}22`, color: engine.color, borderColor: `${engine.color}44` }}>Live</div>
        </div>
      </div>
      <p style={bm.boxDesc}>{engine.desc}</p>
      <ul style={bm.pointsList}>
        {engine.points.map((pt, i) => (
          <li key={i} style={bm.pointItem}><span style={{ ...bm.pointDot, background: engine.color }} />{pt}</li>
        ))}
      </ul>
      <div style={{ display: 'flex', gap: 10, marginTop: 'auto' }}>
        <button
          style={{ ...bm.openBtn, ...glassSurface(engine.color, engine.color, engine.color), flex: 1 }}
          onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.12)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.filter = ''; e.currentTarget.style.transform = ''; }}
          onClick={e => { e.stopPropagation(); onNavigate(engine.route); }}
        >Browse Generators</button>
      </div>
    </div>
  );
}

const bm = {
  grid:     { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 20 },
  box:      { background: 'rgba(255,255,255,0.97)', border: '2px solid rgba(255,255,255,0.12)', borderRadius: 20, padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 14, cursor: 'pointer', transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.15s', height: '100%', boxSizing: 'border-box' },
  boxHover: { transform: 'translateY(-3px)' },
  boxTop:   { display: 'flex', alignItems: 'center', gap: 12 },
  boxIcon:  { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 },
  boxName:  { fontWeight: 800, fontSize: 16, color: '#1a1a1a', lineHeight: 1.3, marginBottom: 4 },
  liveTag:  { display: 'inline-block', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '2px 8px', borderRadius: 20, border: '1px solid' },
  boxDesc:  { fontSize: 13, color: '#6b7280', lineHeight: 1.6, margin: 0 },
  pointsList: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 7, flex: 1 },
  pointItem:  { display: 'flex', alignItems: 'center', gap: 9, fontSize: 12, color: '#374151' },
  pointDot:   { width: 6, height: 6, borderRadius: '50%', flexShrink: 0 },
  openBtn:    { color: '#fff', border: 'none', borderRadius: 10, padding: '11px 0', fontWeight: 700, fontSize: 14, cursor: 'pointer', width: '100%', boxShadow: '0 4px 14px rgba(0,0,0,0.18)', transition: 'filter 0.15s, transform 0.15s', letterSpacing: '0.01em' },
};

// ── Page ───────────────────────────────────────────────────────────
export default function BattlemapsEngines() {
  const navigate = useNavigate();

  return (
    <div style={s.page}>
      <ProductNav />
      <SideHelpPanel title="Battlemaps Creation Suite">
        <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 20, overflowY: 'auto' }}>
          <div>
            <span style={hp.heading}>What This Page Is</span>
            <p style={hp.body}>The Battlemaps Creation Suite gives you access to five connected creation workflows for building complete tactical environments.</p>
          </div>
          <div style={hp.divider} />
          <div style={{ marginTop: 18 }}>
            <span style={hp.heading}>The Five Engines</span>
            <ul style={hp.list}>
              <li style={hp.item}><span style={hp.bold}>Tactical Battlemaps</span> — Full encounter-ready, grid-aligned maps for VTT deployment</li>
              <li style={hp.item}><span style={hp.bold}>Cinematic Scenes</span> — Story-driven visual moments beyond the tactical grid</li>
              <li style={hp.item}><span style={hp.bold}>Structure Assets</span> — Modular buildings and placeable architecture</li>
              <li style={hp.item}><span style={hp.bold}>Props &amp; Objects</span> — Scatter objects and encounter detailing</li>
              <li style={hp.item}><span style={hp.bold}>Terrain Tiles</span> — Seamless tile-based environments across any biome</li>
            </ul>
          </div>
          <div style={hp.divider} />
          <div style={{ marginTop: 18 }}>
            <span style={hp.heading}>How To Use</span>
            <ol style={hp.list}>
              <li style={hp.item}>Choose the creation type that matches your current task</li>
              <li style={hp.item}>Configure selections across the generation layers</li>
              <li style={hp.item}>Generate your output</li>
              <li style={hp.item}>Use Browse to explore available generators within each category</li>
            </ol>
          </div>
          <div style={hp.divider} />
          <div style={{ marginTop: 18 }}>
            <span style={hp.heading}>Tips</span>
            <ul style={hp.list}>
              <li style={hp.item}>Build terrain first, then add structures and props for a complete scene</li>
              <li style={hp.item}>Use the Cinematic creation type for session reveal moments and campaign chapter art</li>
              <li style={hp.item}>All engines draw from the same build pool</li>
            </ul>
          </div>
        </div>
      </SideHelpPanel>

      <div className="vi-desktop-wide" style={s.container}>

        <div style={s.productBar}>
          <div style={s.productIndicator}>🗺️ Battlemaps</div>
          <div style={s.productTabs}>
            <button style={s.productTab} onClick={() => navigate('/dashboard/battlemaps')}>Dashboard</button>
            <button style={{ ...s.productTab, ...s.productTabActive }}>Create</button>
            <button style={s.productTab} onClick={() => navigate('/battlemaps/pricing')}>Plans</button>
          </div>
        </div>

        <div style={s.welcomeBanner}>
          <div>
            <h1 style={s.welcomeTitle}>Battlemaps Creation Suite</h1>
            <p style={s.welcomeSub}>Five connected creation workflows. Choose what you want to build.</p>
          </div>
          <button style={s.upgradeCta} onClick={() => navigate('/battlemaps/pricing')}>View Plans &rarr;</button>
        </div>

        {/* Value strip — same pattern as ecosystemContextLine */}
        <div style={s.valueStrip}>
          <span style={s.valueItem}>🗺️ Tactical Maps</span>
          <span style={s.valueSep}>&bull;</span>
          <span style={s.valueItem}>🌿 Terrain Tiles</span>
          <span style={s.valueSep}>&bull;</span>
          <span style={s.valueItem}>🏰 Structure Assets</span>
          <span style={s.valueSep}>&bull;</span>
          <span style={s.valueItem}>⚗️ Props &amp; Objects</span>
          <span style={s.valueSep}>&bull;</span>
          <span style={s.valueItem}>🎬 Cinematic Scenes</span>
          <span style={s.valueSep}>&bull;</span>
          <span style={s.valueItem}>Deterministic structured output</span>
          <span style={s.valueSep}>&bull;</span>
          <span style={s.valueItem}>VTT-ready deployment</span>
        </div>

        {/* Engine grid — identical to Dashboard.js battlemaps branch */}
        <div style={{ ...bm.grid, marginBottom: 32 }}>
          {BATTLEMAP_ENGINES.map(engine => (
            <BattlemapEngineBox key={engine.key} engine={engine} onNavigate={navigate} />
          ))}
        </div>

        {/* How it works — same card pattern as s.card */}
        <div style={s.ecosystemSection}>
          <div style={s.ecosystemHeader}>
            <div style={s.ecosystemTitle}>How the Creation Workflows Connect</div>
            <div style={s.ecosystemSub}>Each creation type handles a distinct layer of your environment. Use them individually or chain them together as a complete scene-building workflow.</div>
          </div>
          <div style={s.workflowGrid}>
            {[
              { step: '01', icon: '🌿', title: 'Terrain',    desc: 'Start with Terrain Tiles to establish the base landscape, biome, and ground system.' },
              { step: '02', icon: '🏰', title: 'Structures', desc: 'Layer buildings, interiors, and architectural elements over the terrain.' },
              { step: '03', icon: '⚗️', title: 'Props',      desc: 'Detail the scene with scatter assets, encounter objects, and environmental props.' },
              { step: '04', icon: '🗺️', title: 'Map',        desc: 'Compose the complete tactical map — grid-aligned and VTT-ready.' },
              { step: '05', icon: '🎬', title: 'Scene',      desc: 'Finish with a Cinematic Scene for campaign reveals, chapter art, or dramatic story moments.' },
            ].map((item, i) => (
              <div key={i} style={s.workflowStep}>
                <div style={s.workflowNum}>{item.step}</div>
                <div style={s.workflowIcon}>{item.icon}</div>
                <div style={s.workflowTitle}>{item.title}</div>
                <p style={s.workflowDesc}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* What you can create — same ecosystemSection pattern */}
        <div style={s.ecosystemSection}>
          <div style={s.ecosystemHeader}>
            <div style={s.ecosystemTitle}>What You Can Create</div>
            <div style={s.ecosystemSub}>Every output type available across all five creation types.</div>
          </div>
          <div style={s.createGrid}>
            {[
              { icon: '⚔️', title: 'Dungeon Maps',       desc: 'Multi-room dungeon layouts with corridors, chambers, and encounter zones — grid-aligned and VTT-ready.' },
              { icon: '🏙️', title: 'City Environments',  desc: 'District-level urban environments — market quarters, docklands, castle districts — built from structured inputs.' },
              { icon: '🌲', title: 'Encounter Layouts',   desc: 'Tactical zones across any terrain type, from forest ambushes to rooftop chases, configured for your encounter brief.' },
              { icon: '🎬', title: 'Narrative Scenes',    desc: 'Cinematic moments, dramatic establishing shots, and session-reveal imagery for storytelling beyond the grid.' },
              { icon: '📖', title: 'Campaign Visuals',    desc: 'Full campaign visual libraries — chapter headers, location art, faction maps — built through consistent engine inputs.' },
              { icon: '🧱', title: 'Modular Assets',      desc: 'Reusable structure, terrain, and prop assets that slot into any map layout or encounter design workflow.' },
            ].map((item, i) => (
              <div key={i} style={s.createCard}>
                <div style={s.createIcon}>{item.icon}</div>
                <div style={s.createTitle}>{item.title}</div>
                <p style={s.createDesc}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Plan access nudge — same animCard pattern */}
        <div style={s.planNudgeCard}>
          <div style={s.planNudgeLeft}>
            <div style={s.planNudgeIcon}>🗺️</div>
            <div>
              <div style={s.planNudgeTitle}>Full engine access is included in the Creator plan and above.</div>
              <div style={s.planNudgeSub}>Hundreds of generators across all five creation types. Multi-system workflows. Large-scale generation for campaign-volume output.</div>
            </div>
          </div>
          <div style={s.planNudgeRight}>
            <button style={s.planNudgeBtn} onClick={() => navigate('/battlemaps/pricing')}>View Plans &rarr;</button>
            
          </div>
        </div>

        <div style={s.scaleSignal}>5 creation types &bull; hundreds of generators &bull; VTT-ready tactical output</div>

      </div>
    </div>
  );
}

const s = {
  page:             { minHeight: '100vh', background: 'linear-gradient(135deg, #1e1b4b 0%, #334155 50%, #881337 100%)', fontFamily: "'Segoe UI', sans-serif" },
  container:        { maxWidth: 1200, margin: '0 auto', padding: '28px 40px' },
  productBar:       { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  productIndicator: { display: 'flex', alignItems: 'center', gap: 8, borderRadius: 20, padding: '6px 14px', color: '#fff', fontWeight: 700, fontSize: 14, background: 'linear-gradient(135deg, #D4AF37, #b8962e)' },
  productTabs:      { display: 'flex', gap: 8 },
  productTab:       { background: 'rgba(255,255,255,0.06)', border: '2px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', borderRadius: 10, padding: '9px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 700, transition: 'all 0.15s ease' },
  productTabActive: { borderColor: '#D4AF37', color: '#fff', background: 'rgba(212,175,55,0.15)', boxShadow: '0 0 12px rgba(212,175,55,0.3), inset 0 0 0 1px #D4AF37' },
  welcomeBanner:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 16 },
  welcomeTitle:     { color: '#fff', fontSize: 28, fontWeight: 800, margin: '0 0 6px' },
  welcomeSub:       { color: 'rgba(255,255,255,0.6)', fontSize: 15, margin: 0 },
  upgradeCta:       { background: 'linear-gradient(135deg, #D4AF37, #b8962e)', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 24px', fontWeight: 700, fontSize: 15, cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 4px 15px rgba(0,0,0,0.3)' },
  valueStrip:       { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 24, color: 'rgba(255,255,255,0.4)', fontSize: 13, fontStyle: 'italic' },
  valueItem:        { color: 'rgba(255,255,255,0.55)', fontWeight: 600, fontStyle: 'normal' },
  valueSep:         { color: 'rgba(255,255,255,0.2)' },
  ecosystemSection: { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '28px 28px 20px', marginBottom: 24 },
  ecosystemHeader:  { marginBottom: 24 },
  ecosystemTitle:   { color: '#fff', fontWeight: 800, fontSize: 20, marginBottom: 6 },
  ecosystemSub:     { color: 'rgba(255,255,255,0.5)', fontSize: 13 },
  workflowGrid:     { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 },
  workflowStep:     { background: 'rgba(255,255,255,0.97)', borderRadius: 14, padding: '20px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 8 },
  workflowNum:      { fontFamily: 'monospace', fontSize: 10, fontWeight: 700, color: '#D4AF37', letterSpacing: '0.1em', textTransform: 'uppercase' },
  workflowIcon:     { fontSize: 28 },
  workflowTitle:    { fontWeight: 800, fontSize: 14, color: '#1a1a1a' },
  workflowDesc:     { fontSize: 12, color: '#6b7280', lineHeight: 1.55, margin: 0 },
  createGrid:       { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 },
  createCard:       { background: 'rgba(255,255,255,0.97)', borderRadius: 14, padding: '20px 18px', display: 'flex', flexDirection: 'column', gap: 8 },
  createIcon:       { fontSize: 24 },
  createTitle:      { fontWeight: 800, fontSize: 15, color: '#1a1a1a' },
  createDesc:       { fontSize: 13, color: '#6b7280', lineHeight: 1.55, margin: 0 },
  planNudgeCard:    { background: 'rgba(255,255,255,0.92)', borderRadius: 16, padding: '16px 24px', boxShadow: '0 4px 16px rgba(0,0,0,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, marginBottom: 16, flexWrap: 'wrap', border: '1px solid rgba(212,175,55,0.3)' },
  planNudgeLeft:    { display: 'flex', alignItems: 'center', gap: 16, flex: 1 },
  planNudgeIcon:    { width: 44, height: 44, borderRadius: 10, flexShrink: 0, background: 'linear-gradient(135deg, #D4AF37, #b8962e)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 },
  planNudgeTitle:   { fontWeight: 800, fontSize: 15, color: '#1a1a1a', marginBottom: 3 },
  planNudgeSub:     { fontSize: 13, color: '#6b7280' },
  planNudgeRight:   { display: 'flex', gap: 10, flexShrink: 0, flexWrap: 'wrap' },
  planNudgeBtn:     { ...glassSurface('#A66D05', '#D4AF37', '#F0D174'), borderRadius: 10, padding: '11px 22px', fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' },
  planNudgeBtnSecondary: { ...glassSurface('#334155', '#64748B', '#94A3B8'), borderRadius: 10, padding: '11px 18px', fontWeight: 600, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' },
  scaleSignal:      { color: 'rgba(255,255,255,0.3)', fontSize: 12, textAlign: 'center', fontStyle: 'italic', letterSpacing: '0.02em', paddingBottom: 8 },
};

const hp = {
  heading: { fontWeight: 700, fontSize: 13, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 0, marginBottom: 8, display: 'block' },
  body:    { fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5, margin: '0 0 10px 0' },
  list:    { margin: 0, padding: '0 0 4px 16px', display: 'flex', flexDirection: 'column', gap: 0 },
  item:    { fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.55, marginBottom: 6, borderRadius: 6, padding: '2px 4px' },
  bold:    { fontWeight: 700, color: 'rgba(255,255,255,0.88)' },
  divider: { height: 1, background: 'rgba(255,255,255,0.05)', margin: '12px 0' },
};
